/**
 * @license Copyright (c) 2003-2022, RAONWIZ DevTeam. All rights reserved.
 */

import {
	Component,
	NgZone,
	Input,
	Output,
	EventEmitter,
	forwardRef,
	ElementRef,
	AfterViewInit, OnDestroy
} from '@angular/core';

import {
	ControlValueAccessor,
	NG_VALUE_ACCESSOR
} from '@angular/forms';

import { getRaonwizComponentNamespace } from 'raonwiz-integrations-common';

import { RaonkUpload } from './raonkupload';

declare let RAONKUPLOAD: any;
declare let RAONKUpload: any;

@Component( {
	selector: 'RaonkUpload',
	template: '<ng-template></ng-template>',

	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef( () => RaonkUploadComponent ),
			multi: true,
		}
	]
} )

export class RaonkUploadComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {
	/**
	 * The id of the component
	 */
	 @Input() id = "";


	/**
	 * The configuration of the component.
	 */
	@Input() config?: RaonkUpload.Config;

	/**
	 * The component type
	 */
	 @Input() componentType= 'RAONKUPLOAD';

	/**
	 * RAON K Upload script url address. Script will be loaded only if RAONKUPLOAD namespace is missing.
	 */
	@Input() componentUrl = '/raonkupload/js/raonkupload.js';

	/**
	 * Tag name of the component.
	 *
	 * The default tag is `div`.
	 */
	@Input() tagName = 'div';
	
	/**
	 * The runtimes of the component interface.
	 */
	@Input() runtimes: RaonkUpload.Runtimes = RaonkUpload.Runtimes.HTML5;

	/**
	 * Keeps track of the component's data.
	 *
	 * It's also decorated as an input which is useful when not using the ngModel.
	 *
	 * See https://angular.io/api/forms/NgModel to learn more.
	 */
	@Input() set data( data: string ) {
		if ( data === this._data ) {
			return;
		}

		if ( this.instance ) {
			// not supported set data
			// Data may be changed by ACF.
			this._data = RAONKUPLOAD.GetListInfo('array', this.id);
			return;
		}

		this._data = data;
	}

	get data(): string {
		return this._data;
	}

	/**
	 * When set to `true`, the editor becomes view mode.
	 */
	@Input() set viewMode( isViewMode: boolean ) {
		if ( this.instance ) {
			RAONKUPLOAD.SetUploadMode( isViewMode?'view':'edit', this.id );
			return;
		}

		// Delay setting mode
		this._viewMode = isViewMode;
	}

	get viewMode(): boolean {
		if ( this.instance ) {
			let retValue;
			switch(RAONKUPLOAD.GetUploadByName(this.id)._config.mode.toLowerCase()) {
				case 'upload':
				case 'edit':
					retValue = false;
					break;
				default:
					retValue = true;
					break;
			}
			
			return retValue;
		}

		return this._viewMode;
	}

	/**
	 * Fired when the component namespace
	 * is loaded. It only triggers once, no matter how many components are initialised.
	 */
	@Output() namespaceLoaded = new EventEmitter<RaonkUpload.EventInfo>();

	@Output() change = new EventEmitter<RaonkUpload.EventInfo>();
	
	/**
	 * event.
	 */
	@Output() creationComplete = new EventEmitter<RaonkUpload.EventInfo>();
	@Output() beforeAddFile = new EventEmitter<RaonkUpload.EventInfo>();
	@Output() afterAddFile = new EventEmitter<RaonkUpload.EventInfo>();
	@Output() afterAddAllFile = new EventEmitter<RaonkUpload.EventInfo>();
	@Output() beforeDeleteFile = new EventEmitter<RaonkUpload.EventInfo>();
	@Output() afterDeleteFile = new EventEmitter<RaonkUpload.EventInfo>();
	@Output() deleteAllFile = new EventEmitter<RaonkUpload.EventInfo>();
	@Output() beforeUpload = new EventEmitter<RaonkUpload.EventInfo>();
	@Output() uploadComplete = new EventEmitter<RaonkUpload.EventInfo>();
	@Output() beforeDownloadFile = new EventEmitter<RaonkUpload.EventInfo>();
	@Output() beforeOpenFile = new EventEmitter<RaonkUpload.EventInfo>();
	@Output() downloadCompleteFile = new EventEmitter<RaonkUpload.EventInfo>();
	@Output() downloadCompleteAllFile = new EventEmitter<RaonkUpload.EventInfo>();
	@Output() onError = new EventEmitter<RaonkUpload.EventInfo>();
	@Output() uploadingCancel = new EventEmitter<RaonkUpload.EventInfo>();
	@Output() downloadCancel = new EventEmitter<RaonkUpload.EventInfo>();
	@Output() selectItem = new EventEmitter<RaonkUpload.EventInfo>();
	@Output() customAction = new EventEmitter<RaonkUpload.EventInfo>();
	@Output() alert = new EventEmitter<RaonkUpload.EventInfo>();

	/**
	 * A callback executed when the content of the component changes. Part of the
	 * `ControlValueAccessor` (https://angular.io/api/forms/ControlValueAccessor) interface.
	 *
	 * Note: Unset unless the component uses the `ngModel`.
	 */
	onChange?: ( data: string ) => void;

	/**
	 * A callback executed when the component has been blurred. Part of the
	 * `ControlValueAccessor` (https://angular.io/api/forms/ControlValueAccessor) interface.
	 *
	 * Note: Unset unless the component uses the `ngModel`.
	 */
	onTouched?: () => void;

	/**
	 * The instance of the component created.
	 */
	instance: any;

	/**
	 * If the component is view-mode before the component instance is created, it remembers that state,
	 * so the component can become view-mode once it is ready.
	 */
	private _viewMode: boolean = null;

	private _data: string = null;

	private _destroyed: boolean = false;

	constructor( private elementRef: ElementRef, private ngZone: NgZone ) {}

	ngAfterViewInit(): void {
		getRaonwizComponentNamespace( this.componentType, this.componentUrl, namespace => {
			this.namespaceLoaded.emit( namespace );
		} ).then( (namespace) => {
			// Check if component instance was destroyed before `ngAfterViewInit` call (#110).
			// Here, `this.instance` is still not initialized and so additional flag is needed.
			if ( this._destroyed ) {
				return;
			}

			this.ngZone.runOutsideAngular( this.createComponent.bind( this, namespace ) );
		} ).catch( window.console.error );

	}

	ngOnDestroy(): void {
		this._destroyed = true;

		this.ngZone.runOutsideAngular( () => {
			if ( this.instance ) {
				if(RAONKUPLOAD._ExternalComponentMap.has(this.id)) {
					RAONKUPLOAD._ExternalComponentMap.delete(this.id);
				}
				if(RAONKUPLOAD._ExternalEventRetValueMap.has(this.id)) {
					RAONKUPLOAD._ExternalEventRetValueMap.delete(this.id);
				}
				
				RAONKUPLOAD.Destroy(this.id, true);
				this.instance = null;
			}
		} );
	}

	writeValue( value: string ): void {
		this.data = value;
	}

	registerOnChange( callback: ( data: string ) => void ): void {
		this.onChange = callback;
	}

	registerOnTouched( callback: () => void ): void {
		this.onTouched = callback;
	}

	private getComponentUniqueName(): string {
		return "raonkupload_" + Math.random()
		.toString( 36 )
		.replace( /[^a-z]+/g, '' )
		.substr( 0, 5 );
	}

	private createComponent(namespace): void {
		let _uniqueName = this.getComponentUniqueName();
		
		(typeof this.id === 'undefined') && (this.id = _uniqueName);
		
		const element = document.createElement( this.tagName );
		element.id = "componentHolder_" + _uniqueName;
		this.elementRef.nativeElement.appendChild( element );
		
		// view state may change during instance initialization.
		(this._viewMode !== null) && (this.viewMode = this._viewMode);

		// default config
		let _compConfig = {
			Id: this.id,
			UploadHolder: element.id,
			Runtimes: this.runtimes,
			Mode: this._viewMode ? 'view' : 'edit',
			Event: this.subscribe()
		};

		_compConfig = namespace.util.objectExtend( true, this.config, _compConfig );

		// for event callback
		(typeof namespace._ExternalComponentMap === 'undefined') && (namespace._ExternalComponentMap = new Map());
		namespace._ExternalComponentMap.set(this.id, this);

		(typeof namespace._ExternalEventRetValueMap === 'undefined') && (namespace._ExternalEventRetValueMap = new Map());

		var _componentObject = new RAONKUpload( _compConfig );
		_componentObject.name = element.id;
		this.instance = _componentObject;
	}

	private subscribe(): any {
		let _configEvent = {};

		const ComponentEvents = [
			'CreationComplete',
			'BeforeAddFile',
			'AfterAddFile',
			'AfterAddAllFile',
			'BeforeDeleteFile',
			'AfterDeleteFile',
			'DeleteAllFile',
			'BeforeUpload',
			'UploadComplete',
			'BeforeDownloadFile',
			'BeforeOpenFile',
			'DownloadCompleteFile',
			'DownloadCompleteAllFile',
			'OnError',
			'UploadingCancel',
			'DownloadCancel',
			'SelectItem',
			'CustomAction',
			'Alert'
		] as const;

		ComponentEvents.forEach(function(evtName) {
			_configEvent[evtName] = (componentName: any, paramObj: any) => {
				// Set default value
				switch(evtName) {
					case 'BeforeUpload':
					case 'BeforeDownloadFile':
					case 'BeforeOpenFile':
					case 'BeforeAddFile':
					case 'BeforeDeleteFile':
						RAONKUPLOAD._ExternalEventRetValueMap.set(componentName, true);
						break;
				}

				if(RAONKUPLOAD._ExternalComponentMap.has(componentName)) {
					let _this = RAONKUPLOAD._ExternalComponentMap.get(componentName);
					
					_this.ngZone.run( () => {
						switch(evtName) {
							case 'AfterAddAllFile':
							case 'DeleteAllFile':
								if ( _this.onTouched ) {
									_this.onTouched();
								}
								break;
							case 'UploadComplete':
								_this.propagateChange(evtName, { componentName: componentName, paramObj: paramObj });
								break;
						}

						_this[ _this.capitalize(evtName) ].emit( { componentName: componentName, paramObj: paramObj } );
					} );

					// Set default value
					// return event
					let _retValue = "";
					switch(evtName) {
						case 'BeforeUpload':
						case 'BeforeDownloadFile':
						case 'BeforeOpenFile':
						case 'BeforeAddFile':
						case 'BeforeDeleteFile':
							if(RAONKUPLOAD._ExternalEventRetValueMap.has(componentName)) {
								_retValue = RAONKUPLOAD._ExternalEventRetValueMap.get(componentName);
								RAONKUPLOAD._ExternalEventRetValueMap.delete(componentName); // Initialization

								return _retValue;
							}
							break;
					}
				}
			}
		});

		return _configEvent;
	}

	private capitalize(str: string): string {
		return str.charAt(0).toLowerCase() + str.slice(1);
	}

	private propagateChange( eventName: string, eventParams: any ): void {
		this.ngZone.run( () => {
			const newData = RAONKUPLOAD.GetListInfo('array', this.id);
			
			this.change.emit( eventParams );

			if ( newData === this.data ) {
				return;
			}

			this._data = newData;

			if ( this.onChange ) {
				this.onChange( newData );
			}
		} );
	}

}
